// Live preview of the commercial offer next to its fields. It shows the very
// PDF the customer would receive: the backend renders it from the latest
// successful study with the terms currently in the form (saved or not),
// through the same request the download button uses. Nothing is stored and
// no study is re-run; the preview only asks for a PDF when it is open.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, Box, Button, Checkbox, FormControlLabel, IconButton, LinearProgress, Menu, MenuItem, Popover, Stack, Switch, ToggleButton,
  ToggleButtonGroup, Tooltip, Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CloseIcon from "@mui/icons-material/Close";
import ViewAgendaOutlinedIcon from "@mui/icons-material/ViewAgendaOutlined";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import CheckIcon from "@mui/icons-material/Check";
import { OfferColorPicker } from "./CommercialProposalAppearance";
import { metenApi } from "../../services/metenApi";
import { useOptionalDemo } from "../../context/DemoContext";
import { getDemoExportDisabledMessage } from "../../utils/demoAccess";
import {
  DEFAULT_BRAND_COLOR, OFFER_FONTS, OPTIONAL_PROPOSAL_PAGES, commercialOfferValidation, isProjectCommercialMode,
} from "../../components/config/commercialOffer";

export const PREVIEW_DELAY_MS = 1500;
const AUTO_KEY = "pythia.offerPreview.auto";

function readAuto() {
  try {
    return window.localStorage.getItem(AUTO_KEY) !== "false";
  } catch {
    return true;
  }
}

function backendDetail(err) {
  const raw = err?.message || "";
  try {
    const parsed = JSON.parse(raw);
    const detail = parsed?.detail;
    return typeof detail === "string" ? detail : detail?.message || raw;
  } catch {
    return raw;
  }
}

export function previewErrorText(err, isGreek) {
  const detail = backendDetail(err);
  const tr = (el, en) => (isGreek ? el : en);
  if (/not available yet|run a supported study/i.test(detail)) {
    return tr("Η προεπισκόπηση εμφανίζεται μετά από μια επιτυχημένη μελέτη. Εκτελέστε τη μελέτη από τη σελίδα «Προσομοίωση».",
      "The preview appears after a successful study. Run the study from the Simulation page.");
  }
  if (/new supported runs|new behind-the-meter runs|re-run the study|unavailable for this report format/i.test(detail)) {
    return tr("Η προσφορά είναι διαθέσιμη για μελέτες που εκτελέστηκαν μετά την τελευταία αναβάθμιση. Εκτελέστε ξανά τη μελέτη.",
      "The offer is available for studies run after the latest upgrade. Run the study again.");
  }
  return tr(`Δεν ήταν δυνατή η δημιουργία της προεπισκόπησης: ${detail || "άγνωστο σφάλμα"}`,
    `The preview could not be created: ${detail || "unknown error"}`);
}

// onProposalChange(partial) lets the preview change the offer's appearance
// (colour, typeface, pages) directly; without it the preview only shows.
export default function CommercialOfferPreview({ projectId, terms, isGreek, regulatoryMode, onClose, onProposalChange, onPricingChange }) {
  const tr = (el, en) => (isGreek ? el : en);
  const demo = useOptionalDemo();
  const exportMessage = getDemoExportDisabledMessage(demo?.demoProfile, "feasibility");
  const kinds = isProjectCommercialMode(regulatoryMode) ? ["proposal"] : ["proposal", "offer"];
  const [kind, setKind] = useState("proposal");
  const [appendix, setAppendix] = useState(false);
  const [auto, setAuto] = useState(readAuto);
  const [pdfUrl, setPdfUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [pagesAnchor, setPagesAnchor] = useState(null);
  const [colourAnchor, setColourAnchor] = useState(null);
  const [fontAnchor, setFontAnchor] = useState(null);
  const hiddenPages = terms?.proposal?.hidden_pages || [];
  const brandColour = terms?.proposal?.brand_color || "";
  const fontKey = terms?.proposal?.font || "";
  const font = OFFER_FONTS.find((f) => f.value === fontKey) || OFFER_FONTS[0];

  const termsKey = useMemo(() => JSON.stringify(terms || {}), [terms]);
  const latest = useRef({});
  latest.current = { projectId, termsKey, kind, appendix, isGreek };
  const inFlight = useRef(false);
  const pending = useRef(false);
  const alive = useRef(true);
  const urlRef = useRef("");

  const validation = useMemo(
    () => commercialOfferValidation(JSON.parse(termsKey), { isGreek }),
    [termsKey, isGreek],
  );

  const generate = useCallback(async () => {
    const now = latest.current;
    if (!now.projectId || exportMessage) return;
    const problem = commercialOfferValidation(JSON.parse(now.termsKey), { isGreek: now.isGreek });
    if (problem) return;
    // One render at a time: a change made while the server is still busy is
    // picked up by one more request when this one ends, never by a queue.
    if (inFlight.current) {
      pending.current = true;
      return;
    }
    inFlight.current = true;
    setLoading(true);
    setError("");
    try {
      const { blob } = await metenApi.downloadCommercialOffer(
        String(now.projectId), JSON.parse(now.termsKey), { kind: now.kind, appendix: now.appendix });
      if (!alive.current) return;
      const url = URL.createObjectURL(blob);
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      urlRef.current = url;
      setPdfUrl(url);
      setUpdatedAt(new Date());
    } catch (err) {
      if (alive.current) setError(previewErrorText(err, now.isGreek));
    } finally {
      inFlight.current = false;
      if (alive.current) setLoading(false);
      if (alive.current && pending.current) {
        pending.current = false;
        generate();
      }
    }
  }, [exportMessage]);

  // The first preview right away; later changes after a pause in typing.
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      generate();
      return undefined;
    }
    if (!auto) return undefined;
    const timer = window.setTimeout(generate, PREVIEW_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [termsKey, auto, generate]);

  // A different document is a different PDF: fetch it at once.
  const firstKind = useRef(true);
  useEffect(() => {
    if (firstKind.current) {
      firstKind.current = false;
      return;
    }
    generate();
  }, [kind, appendix, generate]);

  useEffect(() => () => {
    alive.current = false;
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
  }, []);

  const toggleAuto = (next) => {
    setAuto(next);
    try {
      window.localStorage.setItem(AUTO_KEY, String(next));
    } catch {
      // storage unavailable: the choice lasts for this visit only
    }
  };

  const time = updatedAt
    ? new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23" }).format(updatedAt)
    : "";

  let notice = null;
  if (!projectId) notice = tr("Αποθηκεύστε το έργο και εκτελέστε τη μελέτη για να δείτε προεπισκόπηση.", "Save the project and run the study to see a preview.");
  else if (exportMessage) notice = exportMessage;
  else if (validation) notice = validation;

  return (
    <Box data-testid="offer-preview" sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 1.5, bgcolor: "background.paper" }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>{tr("Προεπισκόπηση PDF", "PDF preview")}</Typography>
        <Tooltip title={tr("Ανανέωση τώρα", "Refresh now")}>
          <span>
            <IconButton size="small" onClick={generate} disabled={loading || Boolean(notice)} aria-label={tr("Ανανέωση τώρα", "Refresh now")}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={tr("Άνοιγμα σε νέα καρτέλα", "Open in a new tab")}>
          <span>
            <IconButton size="small" disabled={!pdfUrl} onClick={() => window.open(pdfUrl, "_blank", "noopener,noreferrer")}
              aria-label={tr("Άνοιγμα σε νέα καρτέλα", "Open in a new tab")}>
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        {onClose ? (
          <Tooltip title={tr("Κλείσιμο προεπισκόπησης", "Close preview")}>
            <IconButton size="small" onClick={onClose} aria-label={tr("Κλείσιμο προεπισκόπησης", "Close preview")}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}
      </Stack>
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        {kinds.length > 1 ? (
          <ToggleButtonGroup size="small" exclusive value={kind} onChange={(_, value) => value && setKind(value)}
            aria-label={tr("Έγγραφο", "Document")}>
            <ToggleButton value="proposal" sx={{ textTransform: "none" }}>{tr("Αναλυτική προσφορά", "Detailed proposal")}</ToggleButton>
            <ToggleButton value="offer" sx={{ textTransform: "none" }}>{tr("Οικονομική προσφορά", "Commercial offer")}</ToggleButton>
          </ToggleButtonGroup>
        ) : null}
        <FormControlLabel sx={{ ml: 0.5 }} control={<Checkbox size="small" checked={appendix} onChange={(e) => setAppendix(e.target.checked)} />}
          label={<Typography variant="body2">{tr("Με παράρτημα", "With appendix")}</Typography>} />
        <FormControlLabel control={<Switch size="small" checked={auto} onChange={(e) => toggleAuto(e.target.checked)} />}
          label={<Typography variant="body2">{tr("Αυτόματη ανανέωση", "Auto refresh")}</Typography>} />
      </Stack>
      {onProposalChange ? (
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}
          role="group" aria-label={tr("Εμφάνιση προσφοράς", "Offer appearance")}>
          <Typography variant="caption" color="text.secondary">{tr("Εμφάνιση:", "Appearance:")}</Typography>
          <Button size="small" variant="outlined" sx={{ textTransform: "none" }} aria-haspopup="dialog"
            onClick={(e) => setColourAnchor(e.currentTarget)}
            startIcon={<Box component="span" sx={{ width: 14, height: 14, borderRadius: "50%", border: "1px solid rgba(0,0,0,.2)",
              bgcolor: /^#[0-9a-f]{6}$/.test(brandColour) ? brandColour : DEFAULT_BRAND_COLOR }} />}>
            {tr("Χρώμα", "Colour")}
          </Button>
          <Popover open={Boolean(colourAnchor)} anchorEl={colourAnchor} onClose={() => setColourAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}>
            <Box sx={{ p: 2, maxWidth: 470 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>{tr("Χρώμα προσφοράς", "Offer colour")}</Typography>
              <OfferColorPicker value={brandColour} isGreek={isGreek} onChange={(next) => onProposalChange({ brand_color: next })} />
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                {tr("Χρωματίζει τίτλους ενοτήτων, αριθμούς, γραμμές και γραφήματα, στην αναλυτική και στη δισέλιδη προσφορά. Τα σκούρα πλαίσια και το κείμενο μένουν ίδια. Πολύ ανοιχτά χρώματα σκουραίνουν αυτόματα στα κείμενα, ώστε να διαβάζονται.",
                  "Colours section labels, figures, lines and charts, in the detailed proposal and the two-page offer. Dark panels and body text stay the same. Very light colours are darkened automatically in text so it stays readable.")}
              </Typography>
            </Box>
          </Popover>
          <Button size="small" variant="outlined" sx={{ textTransform: "none" }} aria-haspopup="menu"
            aria-label={`${tr("Γραμματοσειρά", "Typeface")}: ${font.label}`}
            startIcon={<TextFieldsIcon fontSize="small" />} onClick={(e) => setFontAnchor(e.currentTarget)}>
            {font.label}
          </Button>
          <Menu anchorEl={fontAnchor} open={Boolean(fontAnchor)} onClose={() => setFontAnchor(null)}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", px: 2, py: 0.5, maxWidth: 300 }}>
              {tr("Όλες έχουν πλήρη ελληνικά και ενσωματώνονται στο PDF.", "All include full Greek and are embedded in the PDF.")}
            </Typography>
            {OFFER_FONTS.map((f) => (
              <MenuItem key={f.value || "default"} dense role="menuitemradio" aria-checked={f.value === fontKey}
                onClick={() => { onProposalChange({ font: f.value }); setFontAnchor(null); }}>
                <Box component="span" sx={{ width: 24, display: "inline-flex" }}>
                  {f.value === fontKey ? <CheckIcon fontSize="small" /> : null}
                </Box>
                <Box component="span" sx={{ fontFamily: f.css }}>{f.label}</Box>
                <Box component="span" sx={{ color: "text.secondary", ml: 1, fontSize: "0.85em" }}>· {isGreek ? f.el : f.en}</Box>
              </MenuItem>
            ))}
          </Menu>
          {kind === "proposal" ? (
            <>
              <Button size="small" variant="outlined" startIcon={<ViewAgendaOutlinedIcon fontSize="small" />} sx={{ textTransform: "none" }}
                onClick={(e) => setPagesAnchor(e.currentTarget)} aria-haspopup="menu">
                {tr("Σελίδες", "Pages")}{hiddenPages.length ? ` · ${OPTIONAL_PROPOSAL_PAGES.length - hiddenPages.length}/${OPTIONAL_PROPOSAL_PAGES.length}` : ""}
              </Button>
              <Menu anchorEl={pagesAnchor} open={Boolean(pagesAnchor)} onClose={() => setPagesAnchor(null)}>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", px: 2, py: 0.5, maxWidth: 280 }}>
                  {tr("Το εξώφυλλο και η οικονομική προσφορά εμφανίζονται πάντα.", "The cover and the commercial offer are always included.")}
                </Typography>
                {OPTIONAL_PROPOSAL_PAGES.map((page) => {
                  const included = !hiddenPages.includes(page.key);
                  return (
                    <MenuItem key={page.key} dense role="menuitemcheckbox" aria-checked={included}
                      onClick={() => onProposalChange({ hidden_pages: included ? [...hiddenPages, page.key] : hiddenPages.filter((k) => k !== page.key) })}>
                      <Checkbox size="small" checked={included} tabIndex={-1} sx={{ p: 0.5, mr: 1 }} />
                      {isGreek ? page.el : page.en}
                    </MenuItem>
                  );
                })}
              </Menu>
            </>
          ) : null}
          {onPricingChange ? (
            <Tooltip describeChild title={tr("Προσθέτει σελίδα με την καθαρή αξία της προσφοράς ανά στοιχείο (εξοπλισμός, εγκατάσταση, λοιπός εξοπλισμός και έργα), χωρίς ΦΠΑ. Ο ΦΠΑ υπολογίζεται μία φορά, στο σύνολο.",
              "Adds a page with the net offer price item by item (equipment, installation, balance of plant), without VAT. VAT is charged once, on the total.")}>
              <FormControlLabel sx={{ ml: 0.5 }}
                control={<Switch size="small" checked={terms?.pricing?.show_cost_breakdown === true}
                  onChange={(e) => onPricingChange({ show_cost_breakdown: e.target.checked })}
                  inputProps={{ "aria-label": tr("Ανάλυση κόστους", "Cost breakdown") }} />}
                label={<Typography variant="body2">{tr("Ανάλυση κόστους", "Cost breakdown")}</Typography>} />
            </Tooltip>
          ) : null}
        </Stack>
      ) : null}
      <Box sx={{ height: 6, mb: 1 }}>{loading ? <LinearProgress /> : null}</Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
        {loading
          ? tr("Δημιουργία προεπισκόπησης…", "Creating the preview…")
          : time
            ? tr(`Ενημερώθηκε ${time}. Δείχνει τα στοιχεία της φόρμας, ακόμη και πριν τα αποθηκεύσετε.`,
              `Updated ${time}. Shows the form as it is now, even before you save.`)
            : tr("Η προεπισκόπηση χρησιμοποιεί τα αποτελέσματα της τελευταίας επιτυχημένης μελέτης.",
              "The preview uses the results of the latest successful study.")}
      </Typography>
      {notice ? <Alert severity="info" sx={{ mb: 1 }}>{notice}</Alert> : null}
      {error ? <Alert severity="warning" sx={{ mb: 1 }}>{error}</Alert> : null}
      {pdfUrl ? (
        <Box component="iframe" title={tr("Προεπισκόπηση προσφοράς", "Offer preview")} src={`${pdfUrl}#view=FitH&navpanes=0`}
          data-testid="offer-preview-frame"
          sx={{ width: "100%", height: { xs: "70vh", lg: "calc(100vh - 260px)" }, minHeight: 420, border: "1px solid", borderColor: "divider", borderRadius: 1, bgcolor: "#f4f6f8" }} />
      ) : null}
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
        {tr("Οι αλλαγές κρατιούνται μόνο με την «Αποθήκευση» του έργου. Η προεπισκόπηση δεν εκτελεί ξανά τη μελέτη.",
          "Changes are kept only when you save the project. The preview does not re-run the study.")}
      </Typography>
    </Box>
  );
}
